<template>
  <Teleport to="body">
    <Transition
      enter-active-class="transition duration-200 ease-out"
      enter-from-class="transform opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition duration-200 ease-in"
      leave-from-class="opacity-100"
      leave-to-class="transform opacity-0"
    >
      <div v-if="modelValue" class="fixed inset-0 z-50 overflow-y-auto" role="dialog" aria-modal="true" @click.self="close">
        <!-- Fond sombre avec flou -->
        <div class="fixed inset-0 bg-black bg-opacity-50 transition-opacity duration-150 ease-in-out backdrop-blur-sm"></div>

        <!-- Contenu du modal -->
        <div class="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
          <div class="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-2xl">
            <div class="bg-white px-4 pb-4 pt-5 sm:p-6">
              <!-- En-tête -->
              <div class="flex items-center justify-between mb-4">
                <div class="flex items-center gap-3">
                  <div class="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-yellow-100">
                    <AlertTriangleIcon class="h-6 w-6 text-yellow-600" />
                  </div>
                  <h3 class="text-lg font-semibold text-gray-900">
                    Agent existant
                  </h3>
                </div>
                <button
                  type="button"
                  class="text-gray-400 hover:text-gray-600"
                  @click="close"
                >
                  <XIcon class="h-6 w-6" />
                </button>
              </div>

              <!-- Message explicatif -->
              <div class="mb-4">
                <p class="text-sm text-gray-600">
                  Un agent public avec cette combinaison <strong>idref_id</strong> et <strong>affiliation_id</strong> existe déjà dans la plateforme.
                </p>
              </div>

              <!-- Carte de l'agent existant -->
              <div class="mb-6">
                <AgentCard
                  v-if="existingAgent"
                  :agent="existingAgent"
                  :view-mode="'default'"
                  :show-edit-button="false"
                  :show-delete-button="false"
                  :is-owned="isOwned"
                  :is-public="true"
                  :show-ownership-badge="true"
                  :user-id="existingAgent?.user_id"
                  :disable-click="true"
                />
              </div>

              <!-- Actions selon le contexte -->
              <div class="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6 gap-3">
                <!-- Action pour WhoStep : ajouter l'agent au mémo -->
                <button
                  v-if="context === 'whoStep'"
                  type="button"
                  class="inline-flex w-full justify-center rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 sm:w-auto"
                  @click="handleUseExisting"
                >
                  Utiliser cet agent
                </button>
                
                <!-- Action pour ImportAlignmentStep : utiliser l'agent public existant -->
                <button
                  v-if="context === 'importAlignment'"
                  type="button"
                  class="inline-flex w-full justify-center rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 sm:w-auto"
                  @click="handleUseExisting"
                >
                  Utiliser l'agent public existant
                </button>
                
                <!-- Action pour contexte default : voir l'agent existant -->
                <button
                  v-if="context === 'default'"
                  type="button"
                  class="inline-flex w-full justify-center rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 sm:w-auto"
                  @click="handleUseExisting"
                >
                  Voir cet agent
                </button>

                <!-- Bouton annuler -->
                <button
                  type="button"
                  class="mt-3 inline-flex w-full justify-center rounded-md bg-white px-4 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                  @click="close"
                >
                  Annuler
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script lang="ts">
import { AlertTriangleIcon, XIcon } from 'lucide-vue-next';
import { computed, defineComponent, type PropType } from 'vue';
import type { AgentWithMemos } from '../../api/types/entities/agent/relations';
import AgentCard from '../cards/AgentCard.vue';
import { useAuthStore } from '../../stores/auth';

export default defineComponent({
  name: 'AgentExistsModal',
  components: {
    AlertTriangleIcon,
    XIcon,
    AgentCard
  },
  props: {
    modelValue: {
      type: Boolean,
      required: true
    },
    existingAgent: {
      type: Object as PropType<AgentWithMemos | null>,
      default: null
    },
    context: {
      type: String as PropType<'whoStep' | 'importAlignment' | 'default'>,
      default: 'default'
    }
  },
  emits: ['update:modelValue', 'use-existing'],
  setup(props, { emit }) {
    const authStore = useAuthStore();
    
    const close = (): void => {
      emit('update:modelValue', false);
    };

    const handleUseExisting = (): void => {
      if (props.existingAgent) {
        emit('use-existing', props.existingAgent);
        close();
      }
    };

    const isOwned = computed((): boolean => {
      if (!props.existingAgent || !authStore.user) {
        return false;
      }
      return props.existingAgent.user_id === authStore.user.uuid;
    });

    return {
      close,
      handleUseExisting,
      isOwned
    };
  }
});
</script>

